'use client';

import React, { useState, useEffect, useRef } from 'react';
import { DiscountDialog } from '@components/Marketing/DiscountDialog';
import { useRouter } from 'next/navigation';
import { api } from '@/trpc/react';
import { useSession } from '@hooks/index';
import { useExperiment, useStatsigClient } from '@statsig/react-bindings';

interface ScrollTriggeredDiscountDialogProps {
  discountCode: string;
  discountPercentage: number;
  scrollTriggerOffset?: number; // Optional: customize scroll trigger point
}

export function ScrollTriggeredDiscountDialog({
  discountCode,
  discountPercentage,
  scrollTriggerOffset = 600 // Default scroll offset
}: ScrollTriggeredDiscountDialogProps) {
  const { push } = useRouter();
  const { isLoggedIn } = useSession();

  // Only fetch discount eligibility for logged-in users
  const { data, isLoading } = api.user.shouldShowDiscountDialog.useQuery(undefined, {
    enabled: isLoggedIn,
    refetchOnWindowFocus: false
  });

  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);

  // Use ref to ensure the scroll handler only runs once
  const scrollHandlerActive = useRef(true);

  // For non-logged in users, always show the dialog
  // For logged-in users, check the query result if 'shouldShowDiscountDialog' property exists
  const isEligibleForDiscount =
    !isLoggedIn ||
    (data !== undefined && 'shouldShowDiscountDialog' in data && (data as any).shouldShowDiscountDialog === true);

  // Setup scroll listener to show discount dialog
  useEffect(() => {
    const handleScroll = () => {
      // Only trigger this once, and only if eligible for discount
      if (
        scrollHandlerActive.current &&
        window.scrollY > scrollTriggerOffset &&
        !isCouponApplied &&
        isEligibleForDiscount
      ) {
        setShowDiscountDialog(true);
        scrollHandlerActive.current = false;
      }
    };

    // Only set up scroll listener if not loading
    if (!isLoading) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }

    return undefined;
  }, [isCouponApplied, scrollTriggerOffset, isEligibleForDiscount, isLoading]);

  // Handle applying the coupon
  const handleApplyCoupon = (applied: boolean) => {
    setIsCouponApplied(applied);
    push(`/pricing?code=${discountCode}`);
  };

  // Handle dialog open state changes
  const handleOpenChange = (open: boolean) => {
    setShowDiscountDialog(open);
  };

  // Don't render anything if not eligible and logged in
  if (isLoggedIn && !isEligibleForDiscount && !isLoading) {
    return null;
  }

  return (
    <DiscountDialog
      discountCode={discountCode}
      discountPercentage={discountPercentage}
      onApplyCoupon={handleApplyCoupon}
      isOpen={showDiscountDialog && !isCouponApplied}
      onOpenChange={handleOpenChange}
    />
  );
}

export function ScrollTriggeredDiscountDialogExperiment(props: ScrollTriggeredDiscountDialogProps) {
  const experiment = useExperiment('landing_page_discount_dialog_conversion');
  const { client } = useStatsigClient();

  const isEligibleForDiscount = experiment.get<boolean>('ShowDiscountDialog');

  console.log('isEligibleForDiscount', isEligibleForDiscount);
  console.log('loadingStatus', client.loadingStatus);
  return isEligibleForDiscount ? <ScrollTriggeredDiscountDialog {...props} /> : null;
}
